import type { ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import type { Release, VitalsSeries } from "@backstory/protocol";
import { useAuth } from "../../../auth/store";
import { useCan } from "../../../auth/roles";
import { ErrorState, Skeleton } from "../../../components/States";
import { useSlowPending, WakingNumbers } from "../WakingNumbers";
import { PageHeader } from "../../../components/ui";
import { callKeys, callsApi } from "../../calls/api";
import { dur, rateText } from "../../calls/components";
import { liveApi, liveKeys } from "../../live/api";
import { insightsApi, insightsKeys } from "../api";
import { LineChart, StatTile, compact, ms, pct, ratingColor, type Rating } from "../charts";
import { Card, MetricWrap, RangePicker, SeverityBadge, kindLabel, useRange } from "../components";

/** web.dev thresholds: good ≤ x, poor > y. */
export function rateVital(name: "lcp" | "inp" | "cls" | "fcp" | "ttfb", v: number | undefined): Rating | undefined {
  if (v === undefined) return undefined;
  const t: Record<string, [number, number]> = { lcp: [2500, 4000], inp: [200, 500], cls: [0.1, 0.25], fcp: [1800, 3000], ttfb: [800, 1800] };
  const [good, poor] = t[name]!;
  return v <= good ? "good" : v > poor ? "poor" : "needs-improvement";
}

function series(v: VitalsSeries | undefined, key: keyof VitalsSeries["series"][number]) {
  return (v?.series ?? []).map((p) => ({ t: p.t, v: (p[key] as number | undefined) ?? null }));
}

function fillFor(rating: Rating | undefined): string {
  if (rating === "good") return "100%";
  if (rating === "needs-improvement") return "58%";
  if (rating === "poor") return "28%";
  return "0%";
}

function VitalChip({ label, value, formatValue, rating }: { label: string; value: number | undefined; formatValue: (v: number) => string; rating: Rating | undefined }) {
  const text = value === undefined ? "—" : formatValue(value);
  return (
    <div className="card vital-chip" role="group" aria-label={`${label}: ${text}`}>
      <span className="label">{label} <span className="muted small">p75</span></span>
      <span className="value" style={{ fontSize: 20, fontWeight: 600, letterSpacing: "-0.02em" }}>{text}</span>
      <span className="bar" aria-hidden="true">
        <i style={{ width: fillFor(rating), background: ratingColor(rating) }} />
      </span>
      <span className="muted small">{rating === "good" ? "Good" : rating === "needs-improvement" ? "Needs improvement" : rating === "poor" ? "Poor" : "—"}</span>
    </div>
  );
}

function DashboardSkeleton() {
  return (
    <div className="viz-tiles dash-hero" aria-busy="true" aria-label="Loading the dashboard">
      {Array.from({ length: 5 }, (_, i) => (
        <div key={i} className="card viz-tile hero" aria-hidden="true">
          <span className="skeleton" style={{ display: "block", height: 12, width: "48%" }} />
          <span className="skeleton" style={{ display: "block", height: 28, width: "62%", marginTop: 16 }} />
        </div>
      ))}
    </div>
  );
}

function MetricLink({ to, search, about, hint, children }: { to: string; search?: Record<string, unknown>; about: string; hint: string; children: ReactNode }) {
  return (
    <MetricWrap about={about} hint={hint}>
      <Link to={to as never} search={search as never} className="metric-link">{children}</Link>
    </MetricWrap>
  );
}

const hints = {
  live: "Sessions connected right now. This ignores the date range.",
  sessions: "Recorded sessions that started in the selected range.",
  users: "Different people in those sessions. A session with nobody identified is left out.",
  errorSessions: "Share of those sessions that recorded a JavaScript error. Good is 1% or less. Poor is above 5%.",
  clusters: "Problems still open, counted once each, including ones already acknowledged. This ignores the date range. The arrow is how many first appeared in the last 24 hours.",
  calls: "Calls in the selected range.",
  quality: "Typical call score, from 0 to 100, based on delay, lost audio, and freezes. Good is 85 or higher. Poor is below 70.",
  poorCalls: "Share of calls scoring under 70. Good is 2% or less. Poor is above 5%.",
  connect: "Share of calls that never connected. Good is 1% or less. Poor is above 5%.",
  setup: "How long the slowest calls took to connect, ignoring the worst 5%. Good is 3 seconds or less. Poor is above 8 seconds.",
  lcp: "How long the main content took to show, for a typical page. Good is 2.5 seconds or less. Poor is above 4 seconds.",
  inp: "How long the page took to react to a tap or click, for a typical interaction. Good is 0.2 seconds or less. Poor is above 0.5 seconds.",
  cls: "How much the page jumped while loading, for a typical view. Good is 0.1 or less. Poor is above 0.25.",
  releaseError: "Share of this release's sessions that recorded a JavaScript error. The arrow compares it with the previous release. Good is 1% or less. Poor is above 5%.",
  releaseClusters: "Problems first seen in this release. The arrow compares it with the previous release. None is good. More than 2 is poor.",
  releaseLcp: "How long the main content took to show for a typical page in this release. The arrow compares it with the previous release. Good is 2.5 seconds or less. Poor is above 4 seconds.",
  releaseInp: "How long the page took to react to a tap or click for a typical interaction in this release. The arrow compares it with the previous release. Good is 0.2 seconds or less. Poor is above 0.5 seconds.",
  releaseCls: "How much the page jumped while loading for a typical view in this release. The arrow compares it with the previous release. Good is 0.1 or less. Poor is above 0.25.",
};

function releaseDelta(
  current: number | undefined,
  previous: number | undefined,
  previousRelease: string | undefined,
  options: { digits?: number; suffix?: string } = {},
) {
  if (current == null || previous == null) return undefined;
  return { value: current - previous, digits: options.digits, suffix: options.suffix, title: `Compared with ${previousRelease}`, upIsGood: false };
}

export function DashboardPage() {
  const auth = useAuth();
  const { range } = useRange();
  const q = useQuery({ queryKey: insightsKeys.overview(range, auth.currentProjectId), queryFn: () => insightsApi.overview(range) });
  const vq = useQuery({ queryKey: insightsKeys.vitals(range, undefined, auth.currentProjectId), queryFn: () => insightsApi.vitals(range), retry: 0 });
  const canAssist = useCan().live;
  const liveQ = useQuery({
    queryKey: liveKeys.sessions(auth.currentProjectId),
    queryFn: liveApi.sessions,
    enabled: canAssist && !!auth.currentProjectId,
    retry: 0,
  });
  const callsQ = useQuery({
    queryKey: [...callKeys.overview(range.from, range.to), auth.currentProjectId],
    queryFn: () => callsApi.overview(range),
    retry: 0,
  });
  const releasesQ = useQuery({
    queryKey: insightsKeys.releases(range, auth.currentProjectId),
    queryFn: () => insightsApi.releases(range),
    retry: 0,
  });

  const slowLoad = useSlowPending(q.isPending);
  const vitalsSlow = useSlowPending(vq.isPending);
  const releasesSlow = useSlowPending(releasesQ.isPending);
  if (q.isPending) {
    return (
      <>
        <PageHeader title="Dashboard" actions={<RangePicker />} />
        {slowLoad ? <WakingNumbers /> : <DashboardSkeleton />}
      </>
    );
  }
  if (q.isError) {
    return (
      <>
        <PageHeader title="Dashboard" actions={<RangePicker />} />
        <div className="card section">
          <ErrorState error={q.error} retry={() => void q.refetch()} />
        </div>
      </>
    );
  }
  const o = q.data;
  const calls = callsQ.data;
  // An empty range comes back as zeros, and a quality of 0 is not a poor call.
  const measuredCalls = calls && calls.calls > 0 ? calls : undefined;
  const releases = [...(releasesQ.data ?? [])].sort((a, b) => new Date(b.lastSeenAt).getTime() - new Date(a.lastSeenAt).getTime());
  const latestRelease: Release | undefined = releases[0];
  const previousRelease: Release | undefined = releases[1];
  const sessTrend = o.seriesSessions.slice(-12).map((p) => p.v);
  return (
    <>
      <PageHeader title="Dashboard" actions={<RangePicker />} />
      <div className="viz-tiles dash-hero">
        {canAssist && (
          <MetricLink to="/live" about="Live now" hint={hints.live}>
            <StatTile hero label="Live now" value={liveQ.data?.length} loading={liveQ.isPending} />
          </MetricLink>
        )}
        <MetricLink to="/" about="Sessions" hint={hints.sessions}>
          <StatTile hero label="Sessions" value={o.sessions} trend={sessTrend} />
        </MetricLink>
        <MetricLink to="/" about="Users" hint={hints.users}>
          <StatTile hero label="Users" value={o.users} />
        </MetricLink>
        <MetricLink to="/issues" search={{ kind: "js_error" }} about="Sessions with errors" hint={hints.errorSessions}>
          <StatTile hero label="Sessions with errors" value={pct(o.errorSessionsPct)} rating={o.errorSessionsPct <= 1 ? "good" : o.errorSessionsPct <= 5 ? "needs-improvement" : "poor"} />
        </MetricLink>
        <MetricLink to="/issues" about="Open issue clusters" hint={hints.clusters}>
          <StatTile hero label="Open issue clusters" value={o.openClusters} delta={{ value: o.newClusters24h, title: `${o.newClusters24h} new in the last 24 hours`, upIsGood: false }} />
        </MetricLink>
      </div>

      <h2 className="page-title" style={{ fontSize: 15, margin: "4px 0 10px" }}>
        WebRTC quality
      </h2>
      {callsQ.isError ? (
        <div className="card section" style={{ marginBottom: 16 }}>
          <ErrorState error={callsQ.error} retry={() => void callsQ.refetch()} />
        </div>
      ) : (
      <div className="viz-tiles">
        <MetricLink to="/calls" about="Calls" hint={hints.calls}><StatTile label="Calls" value={calls?.calls} loading={callsQ.isPending} /></MetricLink>
        <MetricLink to="/calls" about="Call quality" hint={hints.quality}>
          <StatTile
            loading={callsQ.isPending}
            label="Call quality p50"
            value={measuredCalls?.qualityP50}
            rating={measuredCalls?.qualityP50 == null ? undefined : measuredCalls.qualityP50 >= 85 ? "good" : measuredCalls.qualityP50 >= 70 ? "needs-improvement" : "poor"}
          />
        </MetricLink>
        <MetricLink to="/calls" about="Poor-quality calls" hint={hints.poorCalls}>
          <StatTile
            loading={callsQ.isPending}
            label="Poor-quality calls"
            value={measuredCalls ? rateText(measuredCalls.poorQualityRate) : undefined}
            rating={measuredCalls == null ? undefined : measuredCalls.poorQualityRate <= 0.02 ? "good" : measuredCalls.poorQualityRate <= 0.05 ? "needs-improvement" : "poor"}
          />
        </MetricLink>
        <MetricLink to="/calls" about="Connect failures" hint={hints.connect}>
          <StatTile
            loading={callsQ.isPending}
            label="Connect failures"
            value={measuredCalls ? rateText(measuredCalls.connectFailureRate) : undefined}
            rating={measuredCalls == null ? undefined : measuredCalls.connectFailureRate <= 0.01 ? "good" : measuredCalls.connectFailureRate <= 0.05 ? "needs-improvement" : "poor"}
          />
        </MetricLink>
        <MetricLink to="/calls" about="Setup time" hint={hints.setup}>
          <StatTile
            loading={callsQ.isPending}
            label="Setup time p95"
            value={measuredCalls?.setupMsP95}
            formatValue={dur}
            rating={measuredCalls?.setupMsP95 == null ? undefined : measuredCalls.setupMsP95 <= 3_000 ? "good" : measuredCalls.setupMsP95 <= 8_000 ? "needs-improvement" : "poor"}
          />
        </MetricLink>
      </div>
      )}

      <h2 className="page-title" style={{ fontSize: 15, margin: "4px 0 10px" }}>
        Core Web Vitals
      </h2>
      <div className="vital-row">
        <MetricLink to="/releases" about="LCP" hint={hints.lcp}><VitalChip label="LCP" value={o.vitals.lcpP75} formatValue={ms} rating={rateVital("lcp", o.vitals.lcpP75)} /></MetricLink>
        <MetricLink to="/releases" about="INP" hint={hints.inp}><VitalChip label="INP" value={o.vitals.inpP75} formatValue={ms} rating={rateVital("inp", o.vitals.inpP75)} /></MetricLink>
        <MetricLink to="/releases" about="CLS" hint={hints.cls}><VitalChip label="CLS" value={o.vitals.clsP75} formatValue={(v) => v.toFixed(3)} rating={rateVital("cls", o.vitals.clsP75)} /></MetricLink>
      </div>
      {vq.isError && (
        <div className="card section" style={{ marginBottom: 16 }}>
          <ErrorState error={vq.error} retry={() => void vq.refetch()} />
        </div>
      )}
      {vq.isPending && (vitalsSlow ? <WakingNumbers /> : (
        <div className="ins-grid-3">
          <Card title="LCP p75"><Skeleton lines={4} /></Card>
          <Card title="INP p75"><Skeleton lines={4} /></Card>
          <Card title="CLS p75"><Skeleton lines={4} /></Card>
        </div>
      ))}
      {vq.data && vq.data.series.length > 0 && (
        <div className="ins-grid-3">
          <Card title="LCP p75"><LineChart ariaLabel="LCP p75 over time" series={[{ name: "LCP", points: series(vq.data, "lcpP75") }]} formatValue={ms} height={140} noTable /></Card>
          <Card title="INP p75"><LineChart ariaLabel="INP p75 over time" series={[{ name: "INP", points: series(vq.data, "inpP75"), color: "var(--viz-s2)" }]} formatValue={ms} height={140} noTable /></Card>
          <Card title="CLS p75"><LineChart ariaLabel="CLS p75 over time" series={[{ name: "CLS", points: series(vq.data, "clsP75"), color: "var(--viz-s3)" }]} formatValue={(v) => v.toFixed(2)} height={140} noTable /></Card>
        </div>
      )}

      {releasesQ.isError && (
        <div className="card section" style={{ marginBottom: 16 }}>
          <ErrorState error={releasesQ.error} retry={() => void releasesQ.refetch()} />
        </div>
      )}
      {releasesQ.isPending && (releasesSlow ? <WakingNumbers /> : (
        <>
          <h2 className="page-title" style={{ fontSize: 15, margin: "4px 0 10px" }}>Latest release</h2>
          <div className="viz-tiles">
            {["Error rate", "New issues", "LCP p75", "INP p75", "CLS p75"].map((label) => (
              <StatTile key={label} label={label} value={undefined} loading />
            ))}
          </div>
        </>
      ))}
      {latestRelease && (
        <>
          <h2 className="page-title" style={{ fontSize: 15, margin: "4px 0 10px" }}>
            Latest release · <span className="mono">{latestRelease.release}</span>
          </h2>
          <div className="viz-tiles">
            <MetricLink to="/releases" about="release error rate" hint={hints.releaseError}>
              <StatTile
                label="Error rate"
                value={pct(latestRelease.errorRate, 2)}
                delta={releaseDelta(latestRelease.errorRate, previousRelease?.errorRate, previousRelease?.release, { digits: 2, suffix: " pts" })}
                rating={latestRelease.errorRate <= 1 ? "good" : latestRelease.errorRate <= 5 ? "needs-improvement" : "poor"}
              />
            </MetricLink>
            <MetricLink to="/releases" about="new issues" hint={hints.releaseClusters}>
              <StatTile
                label="New issues"
                value={latestRelease.newClusters}
                delta={releaseDelta(latestRelease.newClusters, previousRelease?.newClusters, previousRelease?.release)}
                rating={latestRelease.newClusters === 0 ? "good" : latestRelease.newClusters <= 2 ? "needs-improvement" : "poor"}
              />
            </MetricLink>
            <MetricLink to="/releases" about="release LCP" hint={hints.releaseLcp}>
              <StatTile
                label="LCP p75"
                value={latestRelease.vitals.lcpP75}
                formatValue={ms}
                delta={releaseDelta(latestRelease.vitals.lcpP75, previousRelease?.vitals.lcpP75, previousRelease?.release, { suffix: " ms" })}
                rating={rateVital("lcp", latestRelease.vitals.lcpP75)}
              />
            </MetricLink>
            <MetricLink to="/releases" about="release INP" hint={hints.releaseInp}>
              <StatTile
                label="INP p75"
                value={latestRelease.vitals.inpP75}
                formatValue={ms}
                delta={releaseDelta(latestRelease.vitals.inpP75, previousRelease?.vitals.inpP75, previousRelease?.release, { suffix: " ms" })}
                rating={rateVital("inp", latestRelease.vitals.inpP75)}
              />
            </MetricLink>
            <MetricLink to="/releases" about="release CLS" hint={hints.releaseCls}>
              <StatTile
                label="CLS p75"
                value={latestRelease.vitals.clsP75}
                formatValue={(v) => v.toFixed(3)}
                delta={releaseDelta(latestRelease.vitals.clsP75, previousRelease?.vitals.clsP75, previousRelease?.release, { digits: 3 })}
                rating={rateVital("cls", latestRelease.vitals.clsP75)}
              />
            </MetricLink>
          </div>
        </>
      )}

      <div className="ins-grid-2">
        <Card title="Sessions">
          <LineChart ariaLabel="Sessions over time" series={[{ name: "Sessions", points: o.seriesSessions }]} area />
        </Card>
        <Card title="Errors">
          <LineChart ariaLabel="Errors over time" series={[{ name: "Errors", points: o.seriesErrors, color: "var(--viz-s8)" }]} area />
        </Card>
      </div>

      <div className="ins-grid-2">
        <Card title="Top issues" actions={<Link to="/issues" className="small">All issues →</Link>}>
          <table className="table" aria-label="Top issues">
            <thead><tr><th>Severity</th><th>Issue</th><th style={{ textAlign: "right" }}>24h</th><th style={{ textAlign: "right" }}>Users</th></tr></thead>
            <tbody>
              {o.topIssues.map((c) => (
                <tr key={c.id} className="static" data-testid="top-issue">
                  <td><SeverityBadge severity={c.severity} /></td>
                  <td><Link to="/issues/$clusterId" params={{ clusterId: c.id }} className="ins-title">{c.title}</Link><span className="ins-sub">{kindLabel(c.kind)}{c.route ? ` · ${c.route}` : ""}</span></td>
                  <td className="mono" style={{ textAlign: "right" }}>{c.count24h}</td>
                  <td className="mono" style={{ textAlign: "right" }}>{compact(c.affectedUsers)}</td>
                </tr>
              ))}
              {o.topIssues.length === 0 && (<tr className="static"><td colSpan={4} className="muted" style={{ textAlign: "center", padding: 16 }}>No open issues. Enjoy it.</td></tr>)}
            </tbody>
          </table>
        </Card>
        <Card title="Top routes">
          <table className="table" aria-label="Top routes">
            <thead><tr><th>Route</th><th style={{ textAlign: "right" }}>Sessions</th><th style={{ textAlign: "right" }}>Error %</th><th></th></tr></thead>
            <tbody>
              {o.topRoutes.map((r) => (
                <tr key={r.route} className="static">
                  <td className="mono small ins-title">{r.route}</td>
                  <td className="mono" style={{ textAlign: "right" }}>{r.sessions.toLocaleString()}</td>
                  <td className="mono" style={{ textAlign: "right" }}>{pct(r.errorPct)}</td>
                  <td><Link to="/heatmaps" search={{ route: r.route } as never} className="small">Heatmap →</Link></td>
                </tr>
              ))}
              {o.topRoutes.length === 0 && (<tr className="static"><td colSpan={4} className="muted" style={{ textAlign: "center", padding: 16 }}>No routes yet.</td></tr>)}
            </tbody>
          </table>
        </Card>
      </div>
    </>
  );
}
